import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useAuthContext } from "../../../hooks/useAuthContext";
import studentsAPI from "../../../services/students";
import trainingsAPI from "../../../services/trainings";
import classesAPI from "../../../services/classes";
import slotsAPI from "../../../services/slots";
import teachersAPI from "../../../services/teachers";
import classroomsAPI from "../../../services/classrooms";

const PrintAllStudentsPage = () => {
  const { schoolId, trainingId } = useParams();
  const [searchParams] = useSearchParams();
  const { accessToken } = useAuthContext();
  const [training, setTraining] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);

  // Get filters from URL parameters
  const filters = {
    name: searchParams.get('name') || "",
    class_id: searchParams.get('class_id') || "",
    slot_id: searchParams.get('slot_id') || "",
    teacher_id: searchParams.get('teacher_id') || "",
    classroom_id: searchParams.get('classroom_id') || "",
    min_absences: searchParams.get('min_absences') || "",
    min_retards: searchParams.get('min_retards') || "",
  };

  // Get training data
  const trainingQuery = trainingsAPI.GetOne(schoolId, trainingId, accessToken);
  const trainingData = trainingQuery?.data?.data;

  // Get students with filters
  const studentsQuery = studentsAPI.useGetEnrolledList(
    schoolId,
    trainingId,
    accessToken,
    1,
    1000,
    filters
  );

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch training data
        const trainingResponse = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/v1/schools/${schoolId}/trainings/${trainingId}`,
          {
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + accessToken,
            },
          }
        );
        
        if (trainingResponse.ok) {
          const trainingData = await trainingResponse.json();
          setTraining(trainingData?.data);
        }

        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, [schoolId, trainingId, accessToken]);

  useEffect(() => {
    if (studentsQuery.data?.data) {
      setStudents(studentsQuery.data.data);
    }
  }, [studentsQuery.data]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    
    // Handle DD/MM/YYYY format
    if (typeof dateString === 'string' && dateString.includes('/')) {
      const parts = dateString.split('/');
      if (parts.length === 3) {
        // DD/MM/YYYY -> MM/DD/YYYY for JavaScript Date constructor
        const day = parts[0];
        const month = parts[1];
        const year = parts[2];
        const date = new Date(year, month - 1, day); // month is 0-indexed
        return date.toLocaleDateString('fr-FR');
      }
    }
    
    // Fallback for other date formats
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';
    return date.toLocaleDateString('fr-FR');
  };

  const formatPhone = (phone) => {
    if (!phone) return '-';
    return phone.replace(/(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5');
  };


  const getTeacherName = (student) => {
    const firstAffectation = student.affectations?.[0];
    const teacher = firstAffectation?.teacher;
    
    if (teacher?.member?.name) {
      return teacher.member.name;
    } else if (teacher?.member?.lastname && teacher?.member?.firstname) {
      return `${teacher.member.lastname} ${teacher.member.firstname}`;
    }
    return "-";
  };

  if (loading || studentsQuery.isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        fontSize: '18px'
      }}>
        Chargement...
      </div>
    );
  }


  return (
    <div style={{ 
      fontFamily: 'Arial, sans-serif',
      margin: 0,
      padding: '20px',
      backgroundColor: 'white',
      minHeight: '100vh',
      background: 'white'
    }}>
      <style>
        {`
          html, body {
            background: white !important;
            background-color: white !important;
          }
          
          @media print {
            @page {
              size: A4 landscape;
              margin: 1cm;
            }
            html, body {
              margin: 0;
              padding: 0;
              background: white !important;
              background-color: white !important;
            }
            .page-break {
              page-break-before: always;
            }
            .no-print {
              display: none;
            }
            * {
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            .students-table tr:nth-child(even) {
              background-color: #f9f9f9 !important;
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
              print-color-adjust: exact !important;
              border-top: 1px solid #e0e0e0 !important;
              border-bottom: 1px solid #e0e0e0 !important;
            }
            .students-table tr:nth-child(odd) {
              background-color: white !important;
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
              print-color-adjust: exact !important;
              border-top: 1px solid #f0f0f0 !important;
              border-bottom: 1px solid #f0f0f0 !important;
            }
          }
          
          .students-table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 0;
            font-size: 11px;
          }
          
          .students-table th,
          .students-table td {
            border: 1px solid #333;
            padding: 6px;
            text-align: left;
            vertical-align: top;
          }
          
          .students-table th {
            background-color: #f5f5f5;
            font-weight: bold;
            text-align: center;
          }
          
          .students-table tr:nth-child(even) {
            background-color: #f9f9f9;
            -webkit-print-color-adjust: exact;
            color-adjust: exact;
            print-color-adjust: exact;
            border-top: 1px solid #e0e0e0;
            border-bottom: 1px solid #e0e0e0;
          }
          
          .students-table tr:nth-child(odd) {
            border-top: 1px solid #f0f0f0;
            border-bottom: 1px solid #f0f0f0;
          }
          
          .parent-info {
            min-width: 120px;
          }
          
          .contact-info {
            min-width: 100px;
          }
          
          .print-button {
            position: fixed;
            top: 90px;
            right: 20px;
            background-color: #007bff;
            color: white;
            border: none;
            padding: 15px 30px;
            border-radius: 8px;
            cursor: pointer;
            font-size: 20px;
            font-weight: bold;
            z-index: 1000;
            box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3), 0 2px 4px rgba(0, 0, 0, 0.2);
            transition: all 0.3s ease;
          }
          
          .print-button:hover {
            background-color: #0056b3;
            transform: translateY(-2px);
            box-shadow: 0 8px 20px rgba(0, 0, 0, 0.4), 0 4px 8px rgba(0, 0, 0, 0.3);
          }

        `}
      </style>

      <button 
        className="print-button no-print" 
        onClick={() => window.print()}
      >
        <i className="pi pi-print" style={{ marginRight: '8px' }}></i>
        Imprimer
      </button>

      {/* Header */}
      <div style={{ marginBottom: '20px', textAlign: 'center' }}>
        <h1 style={{ 
          fontSize: '24px', 
          fontWeight: 'bold', 
          margin: '0 0 10px 0',
          color: '#333'
        }}>
          Liste des élèves - {training?.name || 'Formation'}
        </h1>
        
        
        <p style={{ 
          fontSize: '14px', 
          color: '#666', 
          margin: '10px 0 0 0' 
        }}>
          Total: {students.length} élève{students.length > 1 ? 's' : ''}
        </p>
      </div>

      {/* Students Table */}
      {students.length > 0 ? (
        <table className="students-table">
          <thead>
            <tr>
              <th style={{ width: '4%' }}>N°</th>
              <th style={{ width: '12%' }}>Nom de l'élève</th>
              <th style={{ width: '8%' }}>Date de naissance</th>
              <th style={{ width: '6%' }}>Sexe</th>
              <th style={{ width: '10%' }}>Classe</th>
              <th style={{ width: '8%' }}>Créneau</th>
              <th style={{ width: '12%' }}>Enseignant</th>
              <th style={{ width: '8%' }}>Salle</th>
              <th style={{ width: '12%' }}>Père</th>
              <th style={{ width: '8%' }}>Tél. Père</th>
              <th style={{ width: '12%' }}>Mère</th>
              <th style={{ width: '8%' }}>Tél. Mère</th>
            </tr>
          </thead>
          <tbody>
            {students.map((student, index) => {
              const firstAffectation = student.affectations?.[0];
              return (
                <tr key={student.id || index}>
                  <td style={{ textAlign: 'center', fontWeight: 'bold' }}>
                    {index + 1}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <strong>{student.lastname || student?.member?.lastname || ''}</strong> {student.firstname || student?.member?.firstname || ''}
                  </td>
                  <td>{formatDate(student?.birthday || student?.member?.birthday)}</td>
                  <td>{student?.gender === 'B' ? 'Garçon' : student?.gender === 'G' ? 'Fille' : '-'}</td>
                  <td>{student?.classe?.name || '-'}</td>
                  <td>{firstAffectation?.slot?.name || '-'}</td>
                  <td>{getTeacherName(student)}</td>
                  <td>{firstAffectation?.classroom?.name || '-'}</td>
                  <td className="parent-info">
                    {(student?.father_lastname || '') + ' ' + (student?.father_firstname || '') || '-'}
                  </td>
                  <td className="contact-info">
                    {formatPhone(student?.father_phone)}
                  </td>
                  <td className="parent-info">
                    {(student?.mother_lastname || '') + ' ' + (student?.mother_firstname || '') || '-'}
                  </td>
                  <td className="contact-info">
                    {formatPhone(student?.mother_phone)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <h2>Aucun élève trouvé</h2>
        </div>
      )}

      {/* Footer */}
      <div style={{ 
        marginTop: '30px', 
        textAlign: 'center', 
        fontSize: '12px', 
        color: '#666',
        lineHeight: '1.2'
      }}>
        <p style={{ margin: '2px 0', lineHeight: '1.2' }}>Formation: {training?.name || 'Non définie'}</p>
        <p style={{ margin: '2px 0', lineHeight: '1.2' }}>Période: {training?.start_courses_date ? formatDate(training.start_courses_date) : '-'} - {training?.end_courses_date ? formatDate(training.end_courses_date) : '-'}</p>
        <p style={{ margin: '2px 0', lineHeight: '1.2' }}>Généré le: {new Date().toLocaleDateString('fr-FR')}</p>
      </div>
    </div>
  );
};

export default PrintAllStudentsPage;
